import React, { useState } from "react";
import { ArrowRight, ArrowUp, RefreshCw, Sparkles } from "lucide-react";
import { VbButton, Callout, BIG5_LABELS, BIG5_ICONS, type Big5Category } from "@/components/vb";
import { QuestionList } from "./QuestionList";
import { PickerSkeleton } from "./PickerSkeleton";
import { MAX_PER_CATEGORY, type BuyerWizard } from "./useBuyerWizard";

// Icons come from the shared BIG5_ICONS map so the wizard and the verdict show
// the same mark per category.
const CATEGORIES: { key: Big5Category; desc: string }[] = [
  { key: "cost", desc: "What this actually costs" },
  { key: "problems", desc: "Downsides & who it's not for" },
  { key: "head_to_head", desc: "vs. named competitors" },
  { key: "reviews", desc: "What customers say" },
  { key: "best_of", desc: "Category lists AI surfaces" },
];

/** The Big 5 (Marcus Sheridan's "They Ask, You Answer"): the five topics every
 *  buyer researches before they decide. Rich, framework-grounded copy so a user
 *  exploring the greyed placeholder actually grasps why these five and what each
 *  one does. (No em-dashes anywhere in the VB frontend, by house rule.) */
const BIG5_CONTENT: Record<Big5Category, { what: string; asks: string }> = {
  cost: {
    what: "The number one thing buyers research, and the question AI fields most often. Even when the honest answer is “it depends,” naming price, fees, and what drives them up is what earns trust.",
    asks: "How much does it cost? What’s included? Any hidden fees?",
  },
  problems: {
    what: "Buyers actively look for the downsides, the risks, and who a product is wrong for. Owning those honestly is what builds credibility, and it’s exactly what AI repeats when someone asks whether you’re worth it.",
    asks: "What goes wrong? Common complaints? Is this right for me?",
  },
  head_to_head: {
    what: "Straight comparisons against named alternatives. Buyers weigh their options before committing, so how AI frames you against rivals decides a lot. This is where the competitors you listed show up.",
    asks: "X vs Y? Which is better? What are the alternatives?",
  },
  reviews: {
    what: "Third-party proof: what real customers say, ratings, and reputation. Buyers trust other buyers far more than your own claims, and AI aggregates that signal for them.",
    asks: "Is it any good? What do reviews say? Any red flags?",
  },
  best_of: {
    what: "The “best of” and “top” shortlists AI assembles when a buyer asks for the best option in a category. The question that matters is simple: do you make the list at all?",
    asks: "Best in [category]? Top options near me? What would you recommend?",
  },
};

/** Questions section of the create-buyer page. Before generation it shows the
 *  five Big-5 categories as an explorable, greyed placeholder so the user can
 *  click through and learn what each topic covers; "Generate questions" creates
 *  the buyer + drafts the set, after which the same picker holds real questions. */
export function QuestionsStep({ w, onRegenerate }: { w: BuyerWizard; onRegenerate: () => void }) {
  const [active, setActive] = useState<Big5Category>("cost");

  const countFor = (cat: Big5Category) => w.questions.filter((q) => q.category === cat).length;

  // Next-topic affordance (mirrors the persona step's per-field Next button) for
  // users who don't realise the rail is clickable. Shown once the topic is full.
  const nextCategory = (cat: Big5Category): Big5Category | null => {
    const idx = CATEGORIES.findIndex((c) => c.key === cat);
    return idx >= 0 && idx < CATEGORIES.length - 1 ? CATEGORIES[idx + 1].key : null;
  };

  const headerAction = w.hasQuestions ? (
    <VbButton type="button" variant="secondary" onClick={onRegenerate} disabled={w.generating}>
      <RefreshCw size={15} aria-hidden="true" />
      {w.generating ? "Regenerating…" : "Regenerate all"}
    </VbButton>
  ) : (
    <VbButton
      type="button"
      onClick={() => void w.generateQuestions()}
      disabled={!w.personaComplete || w.generating || w.suggesting}
    >
      <Sparkles size={15} aria-hidden="true" />
      {w.generating ? "Generating…" : "Generate questions"}
    </VbButton>
  );

  // Category rail, shared between the real picker and the placeholder. In
  // placeholder mode the badge shows the target ("2") rather than a live count.
  const rail = (placeholder: boolean) => (
    <div className="cats">
      {CATEGORIES.map((c) => {
        const Icon = BIG5_ICONS[c.key];
        return (
        <button
          key={c.key}
          type="button"
          className={`cat-card${active === c.key ? " active" : ""}`}
          onClick={() => setActive(c.key)}
        >
          <span className="ic"><Icon size={16} /></span>
          <span className="body">
            <span className="nm">{BIG5_LABELS[c.key]}</span>
            <span className="desc">{c.desc}</span>
          </span>
          {placeholder ? (
            <span className="ct ct--ghost tnum" title="2 questions per topic">2</span>
          ) : (
            // Green once the topic is full (parity with the persona step's green
            // field checks), so progress reads the same on both halves.
            <span
              className={`ct tnum${countFor(c.key) >= MAX_PER_CATEGORY ? " ct--done" : ""}`}
            >
              {countFor(c.key)}
            </span>
          )}
        </button>
        );
      })}
    </div>
  );

  return (
    <section className="vb-qsection">
      <div className="persona-hd">
        <div className="left">
          <span className="tag">Questions</span>
          <h3>What would they ask AI?</h3>
          <div className="desc">
            The Big&nbsp;5: the five topics every buyer researches before they decide. Click a topic
            to see what it covers.
          </div>
        </div>
        <div className="right">{headerAction}</div>
      </div>

      {w.generating ? (
        <PickerSkeleton rails={5} lines={3} />
      ) : w.hasQuestions ? (
        <div className="picker">
          {rail(false)}
          <div className="panel">
            <QuestionList
              category={active}
              questions={w.questions.filter((q) => q.category === active)}
              onEdit={(id, text) => void w.editQuestion(id, text)}
              onRemove={(id) => void w.removeQuestion(id)}
              onAdd={(text) => void w.addManualQuestion(active, text)}
              busy={w.addingQuestion || w.generating}
            />
            {w.questionError && (
              <div className="limit-flash show" role="alert">
                {w.questionError}
              </div>
            )}
            {(() => {
              const next = nextCategory(active);
              if (!next || countFor(active) === 0) return null;
              return (
                <div className="pf-next">
                  <VbButton type="button" variant="secondary" size="sm" onClick={() => setActive(next)}>
                    Next: {BIG5_LABELS[next]}
                    <ArrowRight size={14} aria-hidden="true" />
                  </VbButton>
                </div>
              );
            })()}
          </div>
        </div>
      ) : (
        // Pre-generation: explorable placeholder. Hint the action above, then the
        // greyed five-category picker the user can click through to learn the Big 5.
        <>
          <Callout
            className="mb-5"
            tone={w.personaComplete ? "brand" : "neutral"}
            dashed={!w.personaComplete}
            role="status"
            icon={w.personaComplete ? <Sparkles size={15} /> : <ArrowUp size={15} />}
          >
            {w.personaComplete
              ? "Ready. Click “Generate questions” to draft all 10, or click a topic to see what it covers."
              : "Finish the buyer above to generate. Preview the topics below in the meantime."}
          </Callout>

          {w.genError && (
            <div className="limit-flash show" role="alert" style={{ marginBottom: 12 }}>
              {w.genError}
            </div>
          )}

          <div className="picker picker--placeholder">
            {rail(true)}
            <div className="panel">
              <div className="panel-hd">
                <div className="row">
                  <h3>{BIG5_LABELS[active]}</h3>
                  <span className="tag">Big 5 buyer topic</span>
                </div>
              </div>
              <p className="q-explain">{BIG5_CONTENT[active].what}</p>
              <div className="q-asks">
                <span className="q-asks__lbl">Buyers ask</span>
                <span className="q-asks__ex">{BIG5_CONTENT[active].asks}</span>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export default QuestionsStep;
